import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Checkbox, Column, Row, Text } from '~/commonComponents';
import { Icon } from '~/commonComponents/Icon';
import { SmartTooltip } from '~/commonComponents/SmartTooltip/SmartTooltip';
import { Colors } from '~/themes/colors';
import { IBadge } from '~/domain/RecommendedProfessionals/recommendedProfessionalsTypes';
import { TEducationMap } from '~/domain/FavoriteProfessionals/favoriteProfessionalsTypes';
import StarIcon from '~/images/icons/StarIcon';
import { ChevronDownIcon } from '~/images/icons/ChevronDownIcon';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import defaultProfileImage from '~/images/defaultProfileImage.png';
import { RatingBreakdown } from './RatingBreakdown';

interface IListItemProps {
  id: string;
  fullName: string;
  isAvailable: boolean;
  isFavorite: boolean;
  isSelected: boolean;
  profileImage: string;
  profession?: string;
  onItemClick: (id: string) => void;
  rating?: {
    averageRating: number;
    professionalSkills?: number;
    interpersonalSkills?: number;
    timeManagement?: number;
  };
  badges: IBadge[];
  education: TEducationMap;
  professions: string[];
  onViewSkills: () => void;
  jobId?: string;
  isInviteSent?: boolean;
}

const ATTENDANCE_BADGE_TYPE = 'attendance-score';
const PHOTO_SIZE = 65;

export const ListItem = ({
  id,
  fullName,
  isAvailable,
  isFavorite,
  isSelected,
  profileImage,
  profession,
  onItemClick,
  rating,
  badges,
  onViewSkills,
  isInviteSent = false,
}: IListItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBadgePopup, setShowBadgePopup] = useState(false);

  const attendanceBadge = badges.find((b) => b.type === ATTENDANCE_BADGE_TYPE);

  const handleRatingToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (rating?.professionalSkills != null) {
      setIsExpanded((prev) => !prev);
    }
  };

  const handleViewSkills = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewSkills();
  };

  return (
    <AnimatePresence>
      <motion.div
        role="presentation"
        data-cy={`invite-popup-row-${id}`}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        layout
        onClick={() => !isInviteSent && onItemClick(id)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '10px 0px',
          borderBottom: `1px solid ${Colors.neutral_100}`,
          cursor: isInviteSent ? 'auto' : 'pointer',
        }}
      >
        <Row alignItems="center" gap={16} width="100%">
          {/* Profile photo with checkbox overlay */}
          <Box position="relative" width={PHOTO_SIZE} height={PHOTO_SIZE} style={{ flexShrink: 0 }}>
            <div
              style={{
                width: PHOTO_SIZE,
                height: PHOTO_SIZE,
                borderRadius: '50%',
                border: isFavorite ? `3px solid ${Colors.favorites_400}` : '3px solid transparent',
                padding: 3,
                boxSizing: 'border-box',
              }}
            >
              <img
                src={profileImage || defaultProfileImage}
                onError={(e) => {
                  if (e.currentTarget.src !== defaultProfileImage) {
                    e.currentTarget.src = defaultProfileImage;
                  }
                }}
                style={{
                  borderRadius: '50%',
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
                alt="profile_image"
              />
            </div>
            {!isInviteSent && (
              <div
                style={{
                  position: 'absolute',
                  top: 6,
                  right: -8,
                  background: 'white',
                  borderRadius: 5,
                  border: '2px solid white',
                  lineHeight: 0,
                }}
              >
                <Checkbox checked={isSelected} style={{ height: 18, width: 18 }} />
              </div>
            )}
          </Box>

          {/* Name + Profession + View Skills */}
          <Column flex={1} gap={2}>
            <Text
              bold
              fontSize={18}
              color={Colors.neutral_600}
              maxWidth={220}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {fullName}
            </Text>
            <Row alignItems="center" gap={4}>
              <Text
                fontSize={16}
                color={isAvailable ? Colors.neutral_500 : Colors.error_500}
              >
                {isAvailable ? profession : 'Currently unavailable'}
              </Text>
              {isAvailable && (
                <>
                  <Text fontSize={16} color={Colors.neutral_500}>
                    &nbsp;{'\u2022'}&nbsp;
                  </Text>
                  <Text
                    fontSize={18}
                    color={Colors.secondary_500}
                    bold
                    style={{ cursor: 'pointer' }}
                    onClick={handleViewSkills}
                  >
                    View Skills
                  </Text>
                </>
              )}
            </Row>
          </Column>

          {/* Right side: Rating + Badge (vertically centered) */}
          <Column alignItems="center" gap={6}>
            {isInviteSent ? (
              <motion.div
                data-cy={`invite-popup-sent-${id}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: 5,
                }}
              >
                <Icon name="check-sent" size={24} />
                <Text color={Colors.neutral_400} fontSize={14}>
                  Sent
                </Text>
              </motion.div>
            ) : (
              <>
                {rating && (
                  <div
                    data-testid="rating-toggle"
                    role="button"
                    tabIndex={0}
                    onClick={handleRatingToggle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRatingToggle(e as any);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      cursor: 'pointer',
                    }}
                  >
                    <StarIcon width={20} height={20} />
                    <Text
                      fontSize={20}
                      bold
                      color={Colors.neutral_600}
                      style={{ textDecoration: 'underline' }}
                    >
                      {rating.averageRating.toFixed(1)}
                    </Text>
                    <div
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <ChevronDownIcon size={20} color={Colors.secondary_500} />
                    </div>
                  </div>
                )}
                {attendanceBadge && (
                  <BadgeInline
                    badge={attendanceBadge}
                    onShowPopup={() => setShowBadgePopup(true)}
                  />
                )}
              </>
            )}
          </Column>
        </Row>

        <AnimatePresence>
          {isExpanded && rating?.professionalSkills != null && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden' }}
              onClick={(e) => e.stopPropagation()}
            >
              <RatingBreakdown
                professionalSkills={rating.professionalSkills}
                interpersonalSkills={rating.interpersonalSkills ?? 0}
                timeManagement={rating.timeManagement ?? 0}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {showBadgePopup && attendanceBadge && (
        // @ts-expect-error ConfirmPopup is a JS component with incomplete types
        <ConfirmPopup
          rightButtonAction={() => setShowBadgePopup(false)}
          rightButtonText="Got it!"
          title={
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: 18,
              }}
            >
              <span className="global_font f-dark f-18" style={{ marginRight: 5 }}>
                {attendanceBadge.popupTitle}
              </span>
              {attendanceBadge.popupTitleIcon && (
                <img
                  src={attendanceBadge.popupTitleIcon}
                  alt="icon"
                  style={{ width: 22, height: 22 }}
                />
              )}
            </div>
          }
          titleStyle={{ marginBottom: 5 }}
          description={attendanceBadge.popupDescription}
          Icon={() =>
            attendanceBadge.popupImageWeb ? (
              <img src={attendanceBadge.popupImageWeb} alt="popup" style={{ width: '50%' }} />
            ) : null
          }
        />
      )}
    </AnimatePresence>
  );
};

interface IBadgeInlineProps {
  badge: IBadge;
  onShowPopup: () => void;
}

const BadgeInline = ({ badge, onShowPopup }: IBadgeInlineProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SmartTooltip
      content={badge.label}
      position="bottom"
      alignment="center"
      trigger="hover"
      className="smart-tooltip"
    >
      <button
        type="button"
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          filter: isHovered ? 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))' : 'none',
          transition: 'filter 0.2s',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          onShowPopup();
        }}
        aria-label={badge.label}
      >
        <img
          src={badge.imageUrl}
          alt={badge.label}
          width={28}
          height={28}
          style={{ borderRadius: '50%' }}
        />
      </button>
    </SmartTooltip>
  );
};
